/**
 * v2 Provisional Forecast — Landing screen.
 *
 * Route: /fdp/provisonal_forecast
 *
 * UX:
 *   - Empty page with a centered suggestive-search field.
 *   - Below the search: a "Recent" panel listing the user's last
 *     few selected concepts (per-user, persisted in localStorage)
 *     for quick re-entry into in-progress workflows. Hidden until
 *     the user has actually selected at least one concept.
 *   - Typing hits the live backend (debounced inside the store).
 *   - Picking a suggestion opens the right-side ConceptDetailSidebar
 *     which lists the NDC universe for the chosen GFC and (when the
 *     GFC has none) offers temp packout NDC creation.
 *   - From the sidebar the user picks the NDC(s) they want and clicks
 *     "Continue", which navigates to the v2 Edit screen with gfc +
 *     ndcs as query params.
 */

import { useEffect } from 'react';
import { observer } from 'mobx-react';

import { useStore } from 'hooks/useStore';
import ConceptSuggestSearch from './ConceptSuggestSearch';
import RecentConcepts from './RecentConcepts';
import ConceptDetailSidebar from '../Sidebar/ConceptDetailSidebar';
import styles from './landing.module.scss';

const ProvisionalForecastV2Landing = observer(() => {
  const {
    ProvisionalForecastV2Store: { loadRecentConcepts },
    UserStore: { userMetadata },
  } = useStore();
  const userEmail = userMetadata?.userInfo?.email ?? null;

  // Hydrate the recent-concept list on mount. Re-runs only when the
  // logged-in user changes (e.g. token refresh that swapped accounts)
  // — within a session this is effectively a one-shot.
  useEffect(() => {
    loadRecentConcepts(userEmail);
  }, [userEmail, loadRecentConcepts]);

  return (
    <div className={styles.landingContainer}>
      <div className={styles.searchWrap}>
        <h1 className={styles.title}>Provisional Forecast</h1>
        <p className={styles.subtitle}>
          Search a Concept or GFC to begin.
        </p>
        <ConceptSuggestSearch />
        <RecentConcepts />
      </div>

      {/* Right-side sidebar — controlled by the v2 store; renders only
          when a concept has been picked from the search dropdown. */}
      <ConceptDetailSidebar />
    </div>
  );
});

export default ProvisionalForecastV2Landing;
